import { BrowserStorageStoreItem, BrowserStorageStoreSettings, Keys } from '../store.types';
import { createStore } from './create-store';

export const createLocalStorageStore = <State extends object, Action extends string>({
    key,
    actions,
    initialState,
    expireInDays,
    excludeProps,
    initAction,
}: BrowserStorageStoreSettings<State, Action>) => {
    const store = createStore<State, Action>({
        initialState,
        actions,
    });

    function updateStore(data: State) {
        return store.dispatch(initAction, data);
    }

    function get(): State | undefined {
        const state = localStorage.getItem(key);
        if (!state) return;

        const parsedState = JSON.parse(state) as BrowserStorageStoreItem<State>;
        if (parsedState.expire && new Date() > parsedState.expire) {
            localStorage.removeItem(key);
            return;
        }

        return parsedState.value;
    }

    function set(state: State) {
        try {
            const now = new Date();
            const serializedState = JSON.stringify({
                value: excludeProps ? excludePropsFromState(state) : state,
                expire: expireInDays ? now.setDate(now.getDate() + expireInDays) : undefined,
            });

            localStorage.setItem(key, serializedState);
        } catch (error) {
            console.log('Error in localStorage.setItem', error);
        }
    }

    function excludePropsFromState(state: State) {
        const keys = Object.keys(state) as Keys<State>;
        const filteredState: State = {} as State;

        keys.filter((key) => !excludeProps?.includes(key)).forEach((key) => {
            const value = state[key];
            filteredState[key] = value;
        });

        return filteredState;
    }

    store.subscribe((data) => set(data));

    const data = get();

    if (data) updateStore(data);

    return { get, set, ...store };
};
